import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createStableContext } from './stable-context.ts';
import { readPreference, writePreference } from './config.ts';

export type ThemeSetting = 'system' | 'light' | 'dark';

type ThemeContext = {
  theme: ThemeSetting;
  resolved: 'light' | 'dark';
  setTheme(theme: ThemeSetting): void;
  privacy: boolean;
  setPrivacy(on: boolean): void;
  /** Amounts typed as on a till, digits from the right (Settings, this device only). */
  cashEntry: boolean;
  setCashEntry(on: boolean): void;
};

const Context = createStableContext<ThemeContext | null>('theme', null);

function systemTheme(): 'light' | 'dark' {
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeSetting>(() => {
    const stored = readPreference('theme');
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  });
  const [system, setSystem] = useState(systemTheme);
  const [privacy, setPrivacyState] = useState(() => readPreference('privacy') === '1');
  const [cashEntry, setCashEntryState] = useState(() => readPreference('cashEntry') === '1');

  useEffect(() => {
    const query = matchMedia('(prefers-color-scheme: dark)');
    const listener = () => setSystem(systemTheme());
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  const resolved = theme === 'system' ? system : theme;
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    root.classList.toggle('privacy', privacy);
  }, [theme, privacy]);

  // A changed theme (not the first paint) cross-fades colours for a moment; see global.css.
  const shown = useRef(resolved);
  useEffect(() => {
    if (shown.current === resolved) return;
    shown.current = resolved;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const root = document.documentElement;
    root.classList.add('theme-changing');
    const timer = setTimeout(() => root.classList.remove('theme-changing'), 360);
    return () => {
      clearTimeout(timer);
      root.classList.remove('theme-changing');
    };
  }, [resolved]);

  const value = useMemo<ThemeContext>(
    () => ({
      theme,
      resolved,
      setTheme(next) {
        writePreference('theme', next === 'system' ? null : next);
        setThemeState(next);
      },
      privacy,
      setPrivacy(on) {
        writePreference('privacy', on ? '1' : null);
        setPrivacyState(on);
      },
      cashEntry,
      setCashEntry(on) {
        writePreference('cashEntry', on ? '1' : null);
        setCashEntryState(on);
      },
    }),
    [theme, resolved, privacy, cashEntry],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTheme(): ThemeContext {
  const context = useContext(Context);
  if (!context) throw new Error('useTheme outside ThemeProvider');
  return context;
}
